/* ------------------------------------------------------------------
   Survey
   ------------------------------------------------------------------
   Three responsibilities, all plain script (no bundler):
   1. Alpine `surveyForm` component — optimistic submit: fetch-POSTs the
      contact form (survey → store email) AND the hidden customer form
      (subscribe name+email, tagged), then shows the thank-you. No reload.
   2. Multi-choice — enforce the per-question max and aggregate the ticked
      labels into one hidden field so the email reads them as one answer.
   3. Slider — position the diamond handle from the native range value,
      write the chosen point's LABEL into the submitting field, and add a
      small "lift + settle" micro-animation while dragging.
   Static mode (reduced motion / theme editor) keeps everything functional
   but skips the micro-animation; the editor also skips the live submit.
------------------------------------------------------------------ */
(function () {
  if (window.__tamreSurveyInit) return;
  window.__tamreSurveyInit = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var staticMode = reduced || designMode;

  /* --- Alpine component --------------------------------------------- */
  document.addEventListener('alpine:init', function () {
    window.Alpine.data('surveyForm', function () {
      return {
        submitted: false,
        sending: false,
        error: false,
        // Enter the full-page thank-you: dissolve the page (.page-lift) via the root
        // class and freeze Lenis. One-way — "Take me home" is a full navigation.
        enterThanks() {
          this.submitted = true;
          document.documentElement.classList.add('survey-thanks');
          if (window.lenis && typeof window.lenis.stop === 'function') window.lenis.stop();
        },
        async submit() {
          if (this.sending) return;
          var form = this.$root.querySelector('.survey__form');
          if (!form) return;

          // In the theme editor, don't create real records — just preview the thanks.
          if (designMode) {
            this.enterThanks();
            return;
          }

          // The submit control is type=button, so native `required` validation never
          // runs on its own — trigger it manually before we post.
          if (!form.reportValidity()) return;

          this.sending = true;
          this.error = false;

          // Submit the contact form via a PLAIN fetch — no token.
          // Per Shopify's captcha docs, form_type `contact` (and `customer`) live in the
          // "Contact" category, governed by the "Contact forms and comments" spam-protection
          // setting, which is OFF for this store — so no CAPTCHA is required. We deliberately
          // do NOT call Shopify.captcha.protect(): it's loaded site-wide (the account pages
          // ARE protected) and calling it would FORCE a captcha onto this unprotected form,
          // inject a failing h-captcha-response, and 400. Button is type=button (no native
          // submit), so there's no /challenge navigation to guard against.
          var ok;
          try {
            var res = await fetch(form.action || window.location.href, {
              method: 'POST',
              body: new URLSearchParams(new FormData(form)),
              redirect: 'follow',
              headers: {
                Accept: 'text/html, */*; q=0.01',
                'X-Requested-With': 'XMLHttpRequest',
              },
            });
            ok = !!(res && res.ok && res.url.indexOf('/challenge') === -1);
          } catch (e) {
            ok = false;
          }

          this.sending = false;
          if (ok) this.enterThanks();
          else this.error = true;
        },
      };
    });
  });

  /* --- Multi-choice: cap selections + aggregate into one field ------ */
  function initChoice(fieldset) {
    if (fieldset.dataset.choiceReady) return;
    fieldset.dataset.choiceReady = '1';

    var max = parseInt(fieldset.dataset.max, 10) || 1;
    var hidden = fieldset.querySelector('[data-choice-value]');
    var boxes = Array.prototype.slice.call(fieldset.querySelectorAll('[data-choice-box]'));

    function sync() {
      var chosen = boxes.filter(function (b) { return b.checked; });
      if (hidden) {
        hidden.value = chosen
          .map(function (b) { return b.value; })
          .join(', ');
      }
      var capped = chosen.length >= max;
      boxes.forEach(function (b) {
        var label = b.closest('.survey__choice');
        var disable = capped && !b.checked;
        b.disabled = disable;
        if (label) label.classList.toggle('is-disabled', disable);
      });
    }

    boxes.forEach(function (b) { b.addEventListener('change', sync); });
    sync();
  }

  /* --- Slider: continuous drag, snap-on-release, submitted label ----- */
  function initSlider(slider) {
    if (slider.dataset.sliderReady) return;
    slider.dataset.sliderReady = '1';

    var input = slider.querySelector('[data-slider-input]');
    var handle = slider.querySelector('[data-slider-handle]');
    var hidden = slider.querySelector('[data-slider-value]');
    var labels = Array.prototype.slice.call(slider.querySelectorAll('[data-slider-label]'));
    if (!input) return;

    var STOPS = 5;
    function leftFor(ratio) { return 10 + ratio * 80; }      // 10%..90% (aligns w/ dots)
    function ratioForValue(v) { return (v - 1) / (STOPS - 1); }
    function nearestValue(ratio) { return Math.round(ratio * (STOPS - 1)) + 1; }
    function labelFor(v) {
      var el = labels[v - 1];
      var t = el ? el.textContent.trim() : '';
      return t || String(v);
    }
    function ratioFromX(clientX) {
      var rect = input.getBoundingClientRect();
      if (!rect.width) return 0;
      return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    }
    function placeHandle(ratio) {
      if (handle) handle.style.left = leftFor(ratio) + '%';
    }
    function commit(v) {
      input.value = String(v);
      if (hidden) hidden.value = labelFor(v);
    }

    // The diamond follows the pointer from the first pixel (no dead zone), then
    // eases to the nearest of the five stops on release — where the value records.
    var dragging = false;
    input.addEventListener('pointerdown', function (e) {
      dragging = true;
      try { input.setPointerCapture(e.pointerId); } catch (err) {}
      e.preventDefault();   // suppress the native stepped thumb; we drive the handle
      input.focus();
      if (handle) {
        handle.classList.add('is-instant');
        if (!staticMode) handle.classList.add('is-dragging');
      }
      var r = ratioFromX(e.clientX);
      placeHandle(r);
      commit(nearestValue(r));
    });
    input.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var r = ratioFromX(e.clientX);
      placeHandle(r);
      commit(nearestValue(r));
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      var r = e && e.clientX != null
        ? ratioFromX(e.clientX)
        : ratioForValue(parseInt(input.value, 10) || 1);
      var v = nearestValue(r);
      commit(v);
      if (handle) {
        handle.classList.remove('is-instant'); // re-enable the glide for the settle
        handle.classList.remove('is-dragging');
        placeHandle(ratioForValue(v));          // ease to the exact stop
      }
    }
    input.addEventListener('pointerup', endDrag);
    input.addEventListener('pointercancel', endDrag);

    // Keyboard (arrow keys) — glide to the stop.
    input.addEventListener('input', function () {
      if (dragging) return; // pointer drives while dragging
      var v = parseInt(input.value, 10) || 1;
      commit(v);
      placeHandle(ratioForValue(v));
    });

    // Initial paint.
    var v0 = parseInt(input.value, 10) || 1;
    commit(v0);
    placeHandle(ratioForValue(v0));
  }

  /* --- Scroll-driven colour transition into the footer -------------- */
  // Independent of the wiring above: if GSAP isn't present the CSS static
  // band is the graceful fallback. Scrubs the footer-coloured panel up as
  // the section scrolls out, so the next colour washes up with scroll.
  var fadeTriggers = [];

  function gsapReady() {
    return window.gsap && window.ScrollTrigger;
  }
  function whenGsapReady(cb) {
    if (gsapReady()) return cb();
    var tries = 0;
    (function poll() {
      if (gsapReady()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function initFade(section) {
    if (staticMode) return; // reduced motion / editor → CSS static band
    if (section.__surveyFadeInit) return;
    section.__surveyFadeInit = true;

    // Invert the whole palette as the footer colour washes up: scrub the section's
    // background cream → footer-gold AND flip the foreground gold → cream in lockstep,
    // so the gold text/outlines/inputs/slider/button don't vanish into the incoming
    // gold — they become cream ink on a gold field, and reverse on scroll-up. Both
    // sides drive off the same CSS vars everything already references, so the whole
    // section follows. Colours come from the schema settings (inline CSS vars).
    var cs = getComputedStyle(section);
    var bgFrom = (cs.getPropertyValue('--survey-bg') || '').trim() || '#f8f4e9';
    var bgTo = (cs.getPropertyValue('--survey-fade-to') || '').trim() || '#937e4f';
    var fgFrom = (cs.getPropertyValue('--survey-fg') || '').trim() || '#937e4f';
    var fgTo = bgFrom; // text/borders end at the original cream

    var lerp = gsap.utils.interpolate;

    // Anchor start AND end to the submit button so it's always a forward range
    // (the button sits close to the section bottom, so ending on the section would
    // invert and snap early). Starts ~1 viewport BEFORE the button reaches the fold
    // and finishes before it enters view — so the button arrives already in the
    // settled inverted palette rather than morphing under the user at the submit moment.
    var startEl = section.querySelector('.survey__actions') || section;

    // Scrub a plain progress proxy and paint both CSS vars in onUpdate (gsap.utils
    // .interpolate handles the colour maths; a proxy avoids depending on GSAP
    // interpolating custom-property colours directly). --survey-bg also drives the
    // section background via the CSS `background-color: var(--survey-bg)`.
    var proxy = { p: 0 };
    var tween = gsap.to(proxy, {
      p: 1,
      ease: 'none',
      onUpdate: function () {
        section.style.setProperty('--survey-bg', lerp(bgFrom, bgTo, proxy.p));
        section.style.setProperty('--survey-fg', lerp(fgFrom, fgTo, proxy.p));
      },
      scrollTrigger: {
        trigger: startEl,
        start: 'top 175%',  // button top ~0.75 viewport below the fold — begins well before submit
        end: '+=50%',      // …completes while the button is still off-screen, so it settles first
        // Small numeric scrub (not `true`) so the colour GLIDES to the scroll-linked
        // target over ~0.4s rather than snapping — masks any residual jump (e.g. from a
        // refresh) as a smooth catch-up instead of a hard flash. No invalidateOnRefresh:
        // this is a plain 0→1 proxy tween (layout-independent), so re-flushing its values
        // on refresh gives nothing and only risks resetting the painted colour mid-scroll.
        scrub: 0.4,
        // The hero above pins its scroll-away (+=120%) and builds that pin AFTER
        // load. Refresh last (priority < the hero's 0) so this measures the button
        // against the correct layout — otherwise the start lands ~1.2 screens early.
        refreshPriority: -1,
      },
    });
    if (tween.scrollTrigger) fadeTriggers.push(tween.scrollTrigger);
  }

  /* --- Scroll reveal: each question fades up + focuses in from blur --- */
  // As a group enters the viewport its parts stagger in (question → desc →
  // answer; multi-choice pills ripple one-by-one). Fires once per group.
  // Independent of the colour scrub: initFade tweens only the --survey-bg/fg
  // custom properties (colour), this tweens opacity/transform/filter — no clash.
  var revealTriggers = [];
  var REVEAL_SEL =
    '.survey__field, .survey__question, .survey__desc, .survey__choice,' +
    ' .survey__slider, .survey__textarea, .survey__submit';

  function initReveal(section) {
    if (staticMode) return; // reduced motion / editor → CSS default (visible)
    if (section.__surveyRevealInit) return;
    section.__surveyRevealInit = true;

    // Activates the motion-only hidden start state in section-survey.css.
    section.classList.add('is-reveal-ready');

    // Groups in scroll order: name/email, each question, the submit.
    var groups = [];
    var identity = section.querySelector('.survey__identity');
    if (identity) groups.push(identity);
    section.querySelectorAll('.survey__block').forEach(function (b) { groups.push(b); });
    var actions = section.querySelector('.survey__actions');
    if (actions) groups.push(actions);

    groups.forEach(function (group) {
      var items = group.querySelectorAll(REVEAL_SEL);
      if (!items.length) return;
      var tween = gsap.to(items, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 1.1,
        ease: 'power3.out',
        stagger: 0.12,
        // Once revealed, tag each part `.is-revealed` (drops it out of the CSS
        // hidden rule) THEN clear the inline props, so the normal cascade resumes —
        // e.g. the multi-choice `.is-disabled` opacity dim can win again (an inline
        // opacity:1 left by GSAP would otherwise beat that stylesheet rule).
        onComplete: function () {
          items.forEach(function (el) { el.classList.add('is-revealed'); });
          gsap.set(items, { clearProps: 'opacity,transform,filter' });
        },
        scrollTrigger: {
          trigger: group,
          start: 'top 85%',
          once: true,          // self-kills after firing — bounded ScrollTrigger count
          refreshPriority: -1, // measure after the hero's pin builds (matches initFade)
        },
      });
      if (tween.scrollTrigger) revealTriggers.push(tween.scrollTrigger);
    });

    // Background watermark: two independent SCRUBBED tweens tied to the wheel, so
    // the motion tracks scroll rather than firing once off-screen. Transform +
    // opacity only (no blur) — it's a large full-width image, keep it GPU-cheap.
    // CSS hides it under `.is-reveal-ready` until these take over inline.
    var bgIcon = section.querySelector('.survey__bg-icon');
    if (bgIcon) {
      // 1) Fade in as it enters — bottom of the viewport → centre.
      var iconFade = gsap.fromTo(
        bgIcon,
        { opacity: 0 },
        {
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: bgIcon,
            start: 'top bottom',
            end: 'top center',
            scrub: 0.5,
            refreshPriority: -1,
          },
        }
      );
      // 2) Parallax — drifts upward across its whole time on screen, so it reads
      // as sitting on a deeper plane than the questions. ~300px total over a full
      // viewport traversal: noticeable but unhurried. Separate tween on `y` only,
      // so it never fights the opacity fade above.
      var iconDrift = gsap.fromTo(
        bgIcon,
        { y: 150 },
        {
          y: -150,
          ease: 'none',
          scrollTrigger: {
            trigger: bgIcon,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.5,
            refreshPriority: -1,
          },
        }
      );
      if (iconFade.scrollTrigger) revealTriggers.push(iconFade.scrollTrigger);
      if (iconDrift.scrollTrigger) revealTriggers.push(iconDrift.scrollTrigger);
    }
  }

  function initFades() {
    gsap.registerPlugin(ScrollTrigger);
    document.querySelectorAll('[data-survey]').forEach(function (s) {
      initFade(s);
      initReveal(s);
    });
  }

  whenGsapReady(initFades);
  window.addEventListener('load', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  /* --- Wiring ------------------------------------------------------- */
  function initSection(section) {
    section.querySelectorAll('[data-choice]').forEach(initChoice);
    section.querySelectorAll('[data-slider]').forEach(initSlider);
  }
  function initAll() {
    document.querySelectorAll('[data-survey]').forEach(initSection);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  // Theme editor re-render.
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-survey]');
    if (!s) return;
    initSection(s);
    fadeTriggers.forEach(function (t) { t.kill(); });
    fadeTriggers = [];
    s.__surveyFadeInit = false;
    revealTriggers.forEach(function (t) { t.kill(); });
    revealTriggers = [];
    s.__surveyRevealInit = false;
    s.classList.remove('is-reveal-ready');
    s.querySelectorAll('.is-revealed').forEach(function (el) { el.classList.remove('is-revealed'); });
    if (gsapReady()) { initFade(s); initReveal(s); }
  });
})();
